import {
  Component,
  DoCheck,
  ElementRef,
  HostBinding,
  Input, OnDestroy,
  Optional,
  Self, ViewChild
} from '@angular/core';
import { ControlValueAccessor, NgControl, NgModel } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';


@Component({
  selector: 'field-phone-number-input',
  templateUrl: './field-phone-number-input.component.html',
  styleUrls: ['./field-phone-number-input.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: FieldPhoneNumberInputComponent
    }
  ]
})
export class FieldPhoneNumberInputComponent implements DoCheck, OnDestroy, MatFormFieldControl<string>, ControlValueAccessor {
  static nextId = 0;

  private _placeholder: string;
  private _required = false;
  private _disabled = false;
  private _value: string;

  @HostBinding() id = `app-phone-input-${FieldPhoneNumberInputComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';
  @ViewChild('inputModel') inputModel: NgModel;

  focused = false;
  errorState = false;
  controlType = 'app-phone-input';

  stateChanges = new Subject<void>();

  onChange: any = () => {};
  onTouched: any = () => {};
  @Input() classes: string;

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  @Input()
  get required() { 
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }

  @Input() get value(): string {
    return this._value;
  }
  set value(value) {
    this._value = value;
    this.stateChanges.next();
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get empty() {
    return !this.value;
  }

  constructor(
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl) {

    // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }

    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  setDisabledState( isDisabled : boolean ) : void {
    this.disabled = isDisabled;
  }

  onBlur () {
    this.ngControl.control.setValue(this.ngControl.control.value);
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }

  writeValue(value: string): void {
    this.value = value;

    this.onChange(value);
    this.onTouched();
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  ngDoCheck(): void {
    if(this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;

      this.stateChanges.next();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

}
